import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import clsx from 'clsx';

import { OfflineBlockedPopup } from '@/components/modals/OfflineBlockedPopup';
import { isRouteOfflineAllowed } from '@/helpers';
import { getSidebarButtonClassName, isSidebarPathActive } from '@/layout/Sidebar/Sidebar.styles';
import { ArrowDownBlackIcon, ArrowUpIcon } from '@/resources';
import { getTestId, TestIds } from '@/shared/test-ids';
import { SidebarItemType } from '@/types';

type SidebarItemProps = {
  sidebarItem: SidebarItemType;
  pathname: string;
  isDropdownOpen: boolean;
  onNavigate: (path: string) => void;
  setOpenDropdown: (itemKey: string | null) => void;
  isOffline?: boolean;
};

export const getSidebarItemKey = (sidebarItem: SidebarItemType) =>
  sidebarItem.path ?? sidebarItem.label;
const toNavTestId = (routeLabel: string) => getTestId(TestIds.nav.base, routeLabel);

export const SidebarItem = ({
  sidebarItem,
  pathname,
  isDropdownOpen,
  onNavigate,
  setOpenDropdown,
  isOffline = false
}: SidebarItemProps) => {
  const [t] = useTranslation('common');
  const [showOfflinePopup, setShowOfflinePopup] = useState(false);

  const itemKey = getSidebarItemKey(sidebarItem);
  const hasChildren = !!sidebarItem.children?.length;
  const isParentActive =
    sidebarItem.children?.some((child) => isSidebarPathActive(pathname, child.path)) ?? false;
  const isItemActive = isSidebarPathActive(pathname, sidebarItem.path);
  const DropdownIcon = isDropdownOpen ? ArrowUpIcon : ArrowDownBlackIcon;
  const dropdownIconClassName = clsx(
    'size-4 -rotate-90 fill-secondary-steel-blue',
    isParentActive ? 'fill-primary-blue' : 'hover:fill-accent-dark-gray'
  );

  const isDisabledOffline = isOffline && !isRouteOfflineAllowed(sidebarItem.path);

  const handleNavigate = (path: string) => {
    if (isOffline && !isRouteOfflineAllowed(path)) {
      setShowOfflinePopup(true);
      return;
    }
    onNavigate(path);
  };

  if (!hasChildren) {
    return (
      <>
        <OfflineBlockedPopup isOpen={showOfflinePopup} onClose={() => setShowOfflinePopup(false)} />
        <button
          type='button'
          data-testid={toNavTestId(sidebarItem.label)}
          onClick={() => sidebarItem.path && handleNavigate(sidebarItem.path)}
          className={clsx(
            getSidebarButtonClassName(isItemActive),
            isDisabledOffline && 'cursor-not-allowed opacity-40'
          )}>
          {sidebarItem.icon}
          <div>{t(sidebarItem.label)}</div>
        </button>
      </>
    );
  }

  return (
    <>
      <OfflineBlockedPopup isOpen={showOfflinePopup} onClose={() => setShowOfflinePopup(false)} />
      <div
        className='relative'
        onMouseEnter={() => !isOffline && setOpenDropdown(itemKey)}
        onMouseLeave={() => setOpenDropdown(null)}>
        <button
          type='button'
          data-testid={toNavTestId(sidebarItem.label)}
          aria-controls={`${itemKey}-sidebar-submenu`}
          aria-expanded={isDropdownOpen}
          onClick={() => isOffline && setShowOfflinePopup(true)}
          className={clsx(
            getSidebarButtonClassName(
              false,
              clsx(
                'relative flex cursor-pointer items-center',
                isParentActive &&
                  '!bg-secondary-light-gray !fill-primary-blue !font-bold !text-primary-blue'
              )
            ),
            isOffline && 'cursor-not-allowed opacity-40'
          )}>
          <span className='flex items-center'>
            {sidebarItem.icon}
            <div>{t(sidebarItem.label)}</div>
          </span>
          <span className='absolute right-2 top-1/2 -translate-y-1/2'>
            <DropdownIcon className={dropdownIconClassName} />
          </span>
        </button>
        <div
          className={clsx(
            'absolute left-0 top-full z-40 h-5 w-full md:left-full md:top-0 md:h-full md:w-5',
            isDropdownOpen ? 'block' : 'hidden'
          )}
        />
        <div
          id={`${itemKey}-sidebar-submenu`}
          className={clsx(
            'absolute left-0 top-full z-50 mt-2 flex w-[210px] flex-col overflow-hidden rounded-lg bg-primary-gray p-2 shadow-lg transition-all duration-300 md:left-full md:top-0 md:ml-5 md:mt-0 md:w-auto',
            isDropdownOpen
              ? 'visible max-h-96 opacity-100'
              : 'pointer-events-none invisible max-h-0 opacity-0'
          )}>
          {sidebarItem.children?.map((child) => {
            const isChildActive = isSidebarPathActive(pathname, child.path);

            return (
              <button
                type='button'
                key={getSidebarItemKey(child)}
                data-testid={toNavTestId(child.label)}
                onClick={(e) => {
                  e.stopPropagation();
                  child.path && handleNavigate(child.path);
                }}
                className={clsx(
                  'mb-1 flex h-9 min-w-max cursor-pointer items-center whitespace-nowrap rounded px-3 text-sm font-normal transition-colors duration-200',
                  isChildActive
                    ? 'bg-secondary-light-gray !font-bold !text-primary-blue'
                    : 'rounded-md text-secondary-steel-blue hover:bg-secondary-light-gray hover:text-accent-dark-gray'
                )}>
                {t(child.label)}
              </button>
            );
          })}
        </div>
      </div>
    </>
  );
};
